import React, { useCallback, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt, faBolt } from '@fortawesome/free-solid-svg-icons';
import { connect } from 'react-redux';
import { listOrders, refireOrder } from '../../services/Ga4PurchaseTrackingService';
import Config from '../Config';

// Server enforces this too (Ga4PurchaseTrackingController::assertAdminAccess)
// - this is just so non-allowed staff don't see a broken/empty page instead
// of a clear reason.
const isAllowedRole = (user_info) => {
  const role = (user_info?.user_type || user_info?.user_role || '').toUpperCase();
  return role === 'ADMIN';
};

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString();
  } catch (e) {
    return d;
  }
};

function Ga4PurchaseTrackingList(props) {
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [dateTo, setDateTo] = useState(new Date());
  const [search, setSearch] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowCount, setRowCount] = useState(0);
  const [limit] = useState(20);
  const [ga4CheckAvailable, setGa4CheckAvailable] = useState(true);
  const [firingId, setFiringId] = useState(null);

  const toApiDate = (d) => d ? d.toISOString().slice(0, 10) : '';

  const fetchOrders = useCallback(async (targetPage = 1) => {
    setLoading(true);
    try {
      const res = await listOrders({
        date_from: toApiDate(dateFrom),
        date_to: toApiDate(dateTo),
        search,
        page: targetPage,
        limit,
      });
      const body = res?.data || {};
      setOrders(body.result || []);
      setRowCount(body.row_count || 0);
      setGa4CheckAvailable(body.ga4_check_available !== false);
      setPage(targetPage);
    } catch (err) {
      console.error('Failed to load orders:', err);
      Config.createNotification('error', 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, search, limit]);

  useEffect(() => {
    fetchOrders(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchOrders(1);
  };

  const askRefire = (order) => {
    const ok = window.confirm(
      `Fire the GA4 purchase event for order ${order.ORDER_ID}? This cannot be undone.`
    );
    if (ok) proceedRefire(order.ORDER_ID);
  };

  const proceedRefire = async (orderId) => {
    setFiringId(orderId);
    try {
      const res = await refireOrder(orderId, false);
      const body = res?.data || {};
      if (body.return_status === 0) {
        Config.createNotification('success', `Purchase event fired for order ${orderId}`);
        fetchOrders(page);
      } else {
        Config.createNotification('error', body.error_message || 'Failed to fire purchase event');
      }
    } catch (err) {
      const body = err?.response?.data;
      Config.createNotification('error', body?.error_message || 'Failed to fire purchase event');
    } finally {
      setFiringId(null);
    }
  };

  if (!isAllowedRole(props.user_info)) {
    return (
      <div className="container-fluid" style={{ padding: 24 }}>
        <div className="alert alert-danger">You are not authorized to view this page.</div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(rowCount / limit));

  return (
    <div className="container-fluid" style={{ padding: 24 }}>
      <div className="row mb-3 align-items-center">
        <div className="col-md-8">
          <h1 className="font-weight-bold" style={{ fontSize: 24 }}>GA4 Purchase Tracking</h1>
          <p className="text-muted" style={{ marginBottom: 0 }}>
            See which orders' purchase event never reached GA4 (e.g. blocked by an ad blocker), and re-fire them manually.
          </p>
        </div>
        <div className="col-md-4 text-right">
          <button className="btn btn-sm btn-outline-primary" onClick={() => fetchOrders(page)} disabled={loading}>
            <FontAwesomeIcon icon={faSyncAlt} /> Refresh
          </button>
        </div>
      </div>

      {!ga4CheckAvailable && (
        <div className="alert alert-warning">
          GA4 duplicate-check is not configured on the server (GA4_PROPERTY_ID / GA4_SERVICE_ACCOUNT_EMAIL /
          GA4_SERVICE_ACCOUNT_PRIVATE_KEY). GA4 status below cannot be verified, and re-firing will be blocked
          until this is set up.
        </div>
      )}

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <form className="form-row align-items-end" onSubmit={handleSearchSubmit}>
            <div className="form-group col-md-3">
              <label>From</label>
              <DatePicker
                selected={dateFrom}
                className="form-control form-control-sm"
                dateFormat="dd-MMM-yyyy"
                onChange={(d) => setDateFrom(d)}
              />
            </div>
            <div className="form-group col-md-3">
              <label>To</label>
              <DatePicker
                selected={dateTo}
                className="form-control form-control-sm"
                dateFormat="dd-MMM-yyyy"
                onChange={(d) => setDateTo(d)}
              />
            </div>
            <div className="form-group col-md-4">
              <label>Search (order id / txn no / customer name / email)</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="e.g. 12345 or jane@example.com"
              />
            </div>
            <div className="form-group col-md-2">
              <button type="submit" className="btn btn-primary btn-sm btn-block" disabled={loading}>
                Search
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          {loading && (<div className="mb-2">Loading...</div>)}
          <div className="table-responsive">
            <table className="table table-striped table-bordered table-sm">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Value</th>
                  <th>Payment Method</th>
                  <th>GA4 Status</th>
                  <th style={{ width: 140 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {orders && orders.length > 0 ? orders.map((o) => (
                  <tr key={o.ORDER_ID}>
                    <td>{o.ORDER_ID}</td>
                    <td>{formatDate(o.ORDER_DATE)}</td>
                    <td>
                      {[o.CUSTOMER_FIRST_NAME, o.CUSTOMER_LAST_NAME].filter(Boolean).join(' ') || '-'}
                      <br />
                      <small className="text-muted">{o.CUSTOMER_EMAIL}</small>
                    </td>
                    <td>{o.CURRENCY} {o.VALUE}</td>
                    <td>{o.PAYMENT_METHOD}</td>
                    <td>
                      {o.GA4_FIRED ? (
                        <span className="badge badge-success">Fired</span>
                      ) : (
                        <span className="badge badge-danger">Missing</span>
                      )}
                    </td>
                    <td>
                      {!o.GA4_FIRED && (
                        <button
                          className="btn btn-sm btn-warning"
                          disabled={firingId === o.ORDER_ID || !ga4CheckAvailable}
                          onClick={() => askRefire(o)}
                        >
                          <FontAwesomeIcon icon={faBolt} /> {firingId === o.ORDER_ID ? 'Firing...' : 'Re-fire'}
                        </button>
                      )}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={7} className="text-center">No orders found for this range</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <span className="text-muted">Page {page} of {totalPages} ({rowCount} orders)</span>
            <div>
              <button
                className="btn btn-sm btn-outline-secondary mr-2"
                disabled={page <= 1 || loading}
                onClick={() => fetchOrders(page - 1)}
              >
                Previous
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page >= totalPages || loading}
                onClick={() => fetchOrders(page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  let user_info = {};
  try {
    user_info = JSON.parse(state.Reducers.user_info);
  } catch (e) {
    user_info = {};
  }
  return { user_info };
};

export default connect(mapStateToProps, null)(Ga4PurchaseTrackingList);
